import React, { useState, useEffect, useContext } from "react";
import {
  Grid,
  Typography,
  makeStyles,
  withStyles,
  Card,
  CardContent,
  CardActions,
  InputLabel,
  Link,
} from "@material-ui/core";
import Select from "react-select-4-glc";
import { useTranslation } from "react-i18next";
import LoadingIndicator from "../../../../Component/LoadingIndicator";
import ModalDialogContext from "../../../../Context/ModalDialog/ModalDialogContext";
import { getClientDocumentTypes } from "../../../../API/Entity/DocumentRequest";
import {
  getAttribute,
  getAttributeValue,
  validateEmail,
} from "../../../../Utilities/Functions";
import ClientUser from "./clientUser";
import RecordInfo from "./recordInfo";
import StepActions from "./stepActions";
import ClientTypeSelector from "./clientTypeSelector";
import styles from "./styles";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const DocumentSelectorStep = (props) => {
  const {
    entity,
    entityAssocaition,
    request,
    lastClientUser,
    clientUsers,
    listOptions,
    reload,
    canSkip,
    onCancel,
    onNext,
    onSkip,
    onBack,
  } = props;
  const { t } = useTranslation();
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(false);
  const [showRecordInfo, setShowRecordInfo] = useState(false);
  const [recordName, setRecordName] = useState();

  const [clientType, setClientType] = useState(null);
  const [clientTypeError, setClientTypeError] = useState("");
  const [coreDocuments, setCoreDocuments] = useState([]);
  const [documentNames, setDocumentNames] = useState("");
  const [coreDocumentOptions, setCoreDocumentOptions] = useState([]);
  const [documentError, setDocumentError] = useState("");
  const [documentNameError, setDocumentNameError] = useState("");
  const [clientUser, setClientUser] = useState(null);
  const [newUsername, setNewUsername] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [userMessage, setUserMessage] = useState("");
  const [userError, setUserError] = useState(false);
  const [attributeChanges, setAttributeChanges] = useState(null);

  const { showErrorMessage } = useContext(ModalDialogContext);

  useEffect(() => {
    const loadDocumentTypes = async (entityId) => {
      try {
        setIsLoading(true);
        const result = await getClientDocumentTypes(entityId);
        const { data = [] } = result || {};
        const options = data.map((d) => ({
          value: d.id,
          label: d.type_name,
        }));
        setCoreDocumentOptions(options);
        setCoreDocuments(
          coreDocuments.filter((dt) =>
            options.find((dto) => dt.id === dto.value),
          ),
        );
      } catch (error) {
        showErrorMessage({ error });
      }
      setIsLoading(false);
    };

    if (entity) {
      loadDocumentTypes(entity.id);
      const { attributes = [] } = entity || {};
      setRecordName(getAttributeValue(getAttribute(attributes, "3")));
    }

    if (!clientType && entity) {
      const { records = [] } = request;
      const r = records.find((r) => r.entity.id === entity.id) || {};
      if (r) {
        setClientType(r.clientType);
      }
    }
  }, [entity, request]);

  useEffect(() => {
    const { records = [] } = request;
    const r = records.find((r) => r.entity.id === entity.id) || {};
    const { documentTypes = {} } = r || {};
    const { coreDocuments = [], documentNames } = documentTypes;
    setCoreDocuments(
      coreDocuments.map((d) => ({ value: d.id, label: d.name })),
    );
    setDocumentNames(documentNames);
  }, [request]);

  const handleClientTypeChange = (ct) => {
    setClientTypeError("");
    setClientType(ct);
  };

  const handleCoreDocChange = (docs) => {
    setCoreDocuments(docs || []);
  };

  const handleDocumentNamesChange = (names) => {
    setDocumentNames(names);
  };

  const onAttributeChange = (changes) => {
    setAttributeChanges(changes);
  };

  const onEntitySave = async () => {
    reload(entity.id);
    setAttributeChanges(null);
  };

  const handleNext = (event) => {
    setClientTypeError("");
    setDocumentError("");
    setUserError(false);
    if (!clientType) {
      setClientTypeError(t("messages.record.documentRequest.clientTypeError"));
      return;
    }
    if (coreDocuments.length === 0) {
      setDocumentError(t("messages.record.documentRequest.documentError"));
      return;
    }
    if (!clientUser) {
      if (!newUsername || !newUserEmail) {
        setUserError({ noUsername: true });
        return;
      } else if (!validateEmail(newUserEmail)) {
        setUserError({ invalidEmail: true });
        return;
      }
    }
    let cu = clientUser;
    if (!cu) {
      cu = { username: newUsername, userEmail: newUserEmail };
    }
    const documentTypes = {
      coreDocuments: coreDocuments.map((d) => ({ id: d.value, name: d.label })),
      documentNames,
    };
    onNext(true, {
      clientType,
      entity,
      entityAssocaition,
      documentTypes,
      clientUser: cu,
      userMessage,
    });
  };

  const handleCancel = (event) => {
    onCancel();
  };

  const handleSkip = (event) => {
    onSkip();
  };

  const handleBack = (event) => {
    onBack();
  };

  const toogleRecordInfo = (e) => {
    e.preventDefault();
    setShowRecordInfo(!showRecordInfo);
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      [],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
  ];

  return (
    <Card variant="outlined">
      <CardContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography>
              {t(
                "labels.record.documentRequest.steps.documentSelectionStep.header",
                {
                  recordName,
                },
              )}{" "}
              &nbsp;
              <Link href="#" component="button" onClick={toogleRecordInfo}>
                {showRecordInfo
                  ? t(
                      "labels.record.documentRequest.steps.documentSelectionStep.hideRecordInfo",
                    )
                  : t(
                      "labels.record.documentRequest.steps.documentSelectionStep.showRecordInfo",
                    )}
              </Link>
            </Typography>
          </Grid>
          {showRecordInfo && (
            <Grid item xs={12}>
              <RecordInfo
                entity={entity}
                listOptions={listOptions}
                onAttributeChange={onAttributeChange}
                onEntitySave={onEntitySave}
              />
            </Grid>
          )}
          <Grid item xs={6}>
            <ClientTypeSelector
              request={request}
              entity={entity}
              clientTypeError={clientTypeError}
              onChange={handleClientTypeChange}
            />
          </Grid>
          {clientType && (
            <Grid item xs={12}>
              <InputLabel className={classes.inputLabel}>
                {t(
                  "labels.record.documentRequest.steps.documentSelectionStep.coreDocuments",
                )}
              </InputLabel>
              <Select
                value={coreDocuments}
                onChange={handleCoreDocChange}
                isSearchable={true}
                isMulti={true}
                isClearable={false}
                options={coreDocumentOptions}
                className={classes.multiSelect}
                menuPortalTarget={document.querySelector(".MuiDialog-root")}
              />
              <div className={classes.errorText}>{documentError}</div>
            </Grid>
          )}
          {clientType && (
            <>
              <Grid item xs={12}>
                <Typography>
                  {t(
                    "labels.record.documentRequest.steps.documentSelectionStep.documentNames",
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} style={{ minHeight: 140 }}>
                <ReactQuill
                  theme="snow"
                  value={documentNames}
                  onChange={handleDocumentNamesChange}
                  modules={modules}
                  formats={formats}
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                />
              </Grid>
            </>
          )}
          {clientType && (
            <Grid item xs={12}>
              <ClientUser
                entity={entity}
                request={request}
                lastClientUser={lastClientUser}
                clientUsers={clientUsers}
                onClientUser={setClientUser}
                onNewUsername={setNewUsername}
                onNewUserEmail={setNewUserEmail}
                onMessage={setUserMessage}
                userError={userError}
              />
            </Grid>
          )}
        </Grid>
      </CardContent>
      <CardActions disableSpacing>
        <StepActions
          canSkip={canSkip}
          disableNext={attributeChanges !== null}
          onCancel={handleCancel}
          onNext={handleNext}
          onBack={handleBack}
          onSkip={handleSkip}
        />
      </CardActions>
    </Card>
  );
};

export default withStyles(styles, { withTheme: true })(DocumentSelectorStep);
